import { useState, useEffect, useRef, useCallback } from 'react';
import _ from 'lodash';
import projectsService from '../../../services/projectsService';
import dataModel from '../../../models/DataModel';

export const PER_PAGE = 10;

export function parseProjectsResponse(response) {
  const data = Array.isArray(response && response.data) ? response.data : [];
  return data
    .filter(Boolean)
    .map(p => (p && p.attributes ? p.attributes : p));
}

export function getTotalPages(response) {
  return (response && response.meta && response.meta.total_pages != null)
    ? Math.max(1, response.meta.total_pages)
    : 1;
}

export default function useAvailableProjectsPicker() {
  const [searchValue, setSearchValue] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [maxPageNo, setMaxPageNo] = useState(null);

  const loadingMoreRef = useRef(false);
  const nextPageRef = useRef(1);
  const hasTriggeredFillLoadRef = useRef(false);

  const fetchProjects = useCallback((pageToLoad, searchString) => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    const trimmed =
      searchString != null && String(searchString).trim() !== ''
        ? String(searchString).trim()
        : null;
    projectsService
      .search(
        trimmed,
        null,
        null,
        null,
        null,
        pageToLoad,
        PER_PAGE,
        null,
        null,
        null,
        true,
        { for_picker: true }
      )
      .then(response => {
        const newProjects = parseProjectsResponse(response);
        const totalPages = getTotalPages(response);
        if (pageToLoad === 1) {
          hasTriggeredFillLoadRef.current = false;
          setProjects(newProjects);
        } else {
          setProjects(prev => {
            const existingIds = new Set(prev.map(p => p && p.id));
            const toAdd = newProjects.filter(p => p && !existingIds.has(p.id));
            return toAdd.length ? [...prev, ...toAdd] : prev;
          });
        }
        nextPageRef.current = pageToLoad + 1;
        setPage(pageToLoad + 1);
        setMaxPageNo(totalPages);
      })
      .catch(() => {
        dataModel.displayWarnErrorMessage('Failed to load projects', 3000, 2);
      })
      .finally(() => {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  }, []);
  
  const loadMoreProjects = useCallback(
    (currentSearchValue = searchValue) => {
      const pageToLoad = nextPageRef.current;
      if (pageToLoad <= 1) return;
      fetchProjects(pageToLoad, currentSearchValue);
    },
    [fetchProjects, searchValue]
  );

  const debouncedSearch = useCallback(
    _.debounce(searchString => fetchProjects(1, searchString), 400),
    [fetchProjects]
  );

  useEffect(() => {
    nextPageRef.current = 1;
    fetchProjects(1, null);
  }, [fetchProjects]);

  useEffect(() => {
    if (searchValue || loadingMore || hasTriggeredFillLoadRef.current) return;
    if (projects.length < PER_PAGE) return;
    if (maxPageNo !== null && maxPageNo <= 1) return;
    hasTriggeredFillLoadRef.current = true;
    const t = setTimeout(() => loadMoreProjects(searchValue), 400);
    return () => clearTimeout(t);
  }, [
    projects.length,
    searchValue,
    loadingMore,
    maxPageNo,
    loadMoreProjects,
  ]);

  const handleSearchChange = useCallback(
    searchString => {
      const value = searchString != null ? String(searchString) : '';
      setSearchValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const hasMore = maxPageNo === null || page <= maxPageNo;

  return {
    projects,
    searchValue,
    loadingMore,
    page,
    maxPageNo,
    loadMoreProjects: () => loadMoreProjects(searchValue),
    handleSearchChange,
    hasMore,
  };
}



// WEBPACK FOOTER //
// ./src/containers/ProjectSummaryContainer/AddProjectGroupPopup/useAvailableProjectsPicker.js